import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Card, TextInput, Button, Text, Alert, Stack, Select, Group, Menu, UnstyledButton,
} from '@mantine/core'
import { IconAlertCircle, IconCheck, IconMail, IconMapPin, IconUser, IconBuilding, IconId, IconChevronDown } from '@tabler/icons-react'
import { PhoneInputIntl } from '@/components/ui/PhoneInputIntl'
import { CepInput } from '@/components/ui/CepInput'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { registrationApi } from '@/api/registration'
import type { OrganizationOption, RegistrationPayload } from '@/api/registration'
import { formatApiError } from '@/api/errors'

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
    '&:focus': { borderColor: 'var(--ai-brand-verde)' },
  },
}

interface CountryDDI {
  code: string
  flag: string
  ddi: string
  name: string
  maxDigits: number
  format: (digits: string) => string
}

const COUNTRIES: CountryDDI[] = [
  {
    code: 'BR', flag: '🇧🇷', ddi: '+55', name: 'Brasil', maxDigits: 11,
    format: (d) => {
      if (d.length <= 2) return d.length ? `(${d}` : ''
      if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
    },
  },
  {
    code: 'US', flag: '🇺🇸', ddi: '+1', name: 'Estados Unidos', maxDigits: 10,
    format: (d) => {
      if (d.length <= 3) return d.length ? `(${d}` : ''
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    },
  },
  {
    code: 'PT', flag: '🇵🇹', ddi: '+351', name: 'Portugal', maxDigits: 9,
    format: (d) => {
      if (d.length <= 3) return d
      if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`
      return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`
    },
  },
  {
    code: 'AR', flag: '🇦🇷', ddi: '+54', name: 'Argentina', maxDigits: 10,
    format: (d) => {
      if (d.length <= 2) return d
      if (d.length <= 6) return `${d.slice(0, 2)} ${d.slice(2)}`
      return `${d.slice(0, 2)} ${d.slice(2, 6)}-${d.slice(6)}`
    },
  },
  {
    code: 'CO', flag: '🇨🇴', ddi: '+57', name: 'Colombia', maxDigits: 10,
    format: (d) => {
      if (d.length <= 3) return d
      if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`
      return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`
    },
  },
]

function formatLocalPhone(value: string, country: CountryDDI): string {
  const digits = value.replace(/\D/g, '').slice(0, country.maxDigits)
  return country.format(digits)
}

function buildFullPhone(localPhone: string, country: CountryDDI): string {
  const digits = localPhone.replace(/\D/g, '')
  if (!digits) return ''
  return `${country.ddi} ${country.format(digits)}`
}

export function Register() {
  const navigate = useNavigate()
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [country, setCountry] = useState<CountryDDI>(COUNTRIES[0])
  const [localPhone, setLocalPhone] = useState('')
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    organization_id: '',
    employee_number: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
  })
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  // Payload aguardando confirmacao de reabertura. Guarda o que a pessoa DIGITOU —
  // nunca dado vindo do servidor: /register e publico e ecoar a submissao anterior
  // entregaria PII a quem apenas digitou o e-mail dela.
  const [revisao, setRevisao] = useState<RegistrationPayload | null>(null)

  // O dropdown so carrega depois de um e-mail do dominio: a lista de organizacoes e a
  // carteira de clientes, e ate 08/09/2026 ela era publica para qualquer um
  // (TASK-10487). O efeito depende do e-mail e nao roda na montagem.
  useEffect(() => {
    const email = form.email.trim()
    if (!email || emailError) {
      setOrganizations([])
      return
    }
    let cancelado = false
    registrationApi
      .listOrganizations(email)
      .then((orgs) => { if (!cancelado) setOrganizations(orgs) })
      .catch(() => { if (!cancelado) setOrganizations([]) })
    return () => { cancelado = true }
  }, [form.email, emailError])

  const validateEmail = (email: string) => {
    if (!email) { setEmailError(''); return }
    const domain = email.trim().toLowerCase().split('@')[1]
    if (domain && domain !== 'amazonasinovare.com.br') {
      setEmailError('Apenas emails @amazonasinovare.com.br são aceitos')
    } else {
      setEmailError('')
    }
  }

  /**
   * Envia o cadastro. Se a API pedir confirmacao de reabertura, troca para a tela de
   * revisao em vez de mostrar erro.
   *
   * O `code` do `detail` e lido ANTES do `formatApiError`: ele devolveria a `message`
   * certa na caixa de erro — e a pessoa ficaria lendo um aviso sem ter o que fazer,
   * que e exatamente o beco de hoje.
   */
  const enviar = async (payload: RegistrationPayload) => {
    setLoading(true)
    try {
      await registrationApi.submit(payload)
      setRevisao(null)
      setSuccess(true)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: { code?: string } } } })
        ?.response?.data?.detail
      if (detail?.code === 'reopen_required') {
        setRevisao(payload)
        return
      }
      setRevisao(null)
      setError(formatApiError(err, 'Erro ao enviar cadastro. Tente novamente.'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (emailError) return
    if (!form.full_name || !form.email || !form.phone || !form.address || !form.organization_id || !form.employee_number) {
      setError('Preencha todos os campos.')
      return
    }
    setError('')
    await enviar({
      full_name: form.full_name,
      email: form.email.trim().toLowerCase(),
      phone: form.phone,
      address: form.address,
      organization_id: Number(form.organization_id),
      employee_number: form.employee_number,
      cep: form.cep || undefined,
      logradouro: form.logradouro || undefined,
      numero: form.numero || undefined,
      complemento: form.complemento || undefined,
      bairro: form.bairro || undefined,
      cidade: form.cidade || undefined,
      uf: form.uf || undefined,
    })
  }

  if (revisao) {
    const organizacao = organizations.find((o) => o.id === revisao.organization_id)?.name ?? ''
    // SO o que a pessoa acabou de digitar. Nada do servidor: nem o motivo da recusa,
    // nem quem decidiu, nem a data — isso vive no e-mail, que so a dona da caixa le.
    const linhas: [string, string][] = [
      ['Nome', revisao.full_name],
      ['E-mail', revisao.email],
      ['Telefone', revisao.phone],
      ['Organização', organizacao],
      ['Matrícula', revisao.employee_number],
      ['Endereço', revisao.address],
    ]
    return (
      <Box style={{ minHeight: '100vh', background: 'var(--ai-auth-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Box style={{ width: '100%', maxWidth: 440 }}>
          <Stack align="center" mb="xl" gap={4}>
            <BrandLogo variant="horizontal" color="white" width={180} />
          </Stack>
          <Card radius="md" p="xl" style={{ backgroundColor: 'var(--ai-auth-card-bg)', border: '1px solid var(--ai-auth-border)', boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
            <Stack gap="lg">
              <Stack gap={4}>
                <Text fw={700} size="lg" c="#e9ecef">Confira seus dados</Text>
                <Text size="sm" c="#8fa0aa">
                  Você já enviou uma solicitação antes. Confirme para reenviá-la com os
                  dados atuais.
                </Text>
              </Stack>

              <Stack gap={10}>
                {linhas.map(([rotulo, valor]) => (
                  <Group key={rotulo} justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                    <Text size="xs" fw={600} c="#8fa0aa" style={{ flexShrink: 0 }}>{rotulo}</Text>
                    <Text size="sm" c="#e9ecef" ta="right" style={{ wordBreak: 'break-word' }}>
                      {valor || '—'}
                    </Text>
                  </Group>
                ))}
              </Stack>

              <Group grow>
                <Button
                  variant="default"
                  onClick={() => setRevisao(null)}
                  disabled={loading}
                >
                  Corrigir
                </Button>
                <Button
                  loading={loading}
                  onClick={() => enviar({ ...revisao, confirm_reopen: true })}
                  style={{ backgroundColor: 'var(--ai-brand-verde)' }}
                >
                  Confirmar reenvio
                </Button>
              </Group>
            </Stack>
          </Card>
        </Box>
      </Box>
    )
  }

  if (success) {
    return (
      <Box style={{ minHeight: '100vh', background: 'var(--ai-auth-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Box style={{ width: '100%', maxWidth: 440 }}>
          <Stack align="center" mb="xl" gap={4}>
            <BrandLogo variant="horizontal" color="white" width={180} />
          </Stack>
          <Card radius="md" p="xl" style={{ backgroundColor: 'var(--ai-auth-card-bg)', border: '1px solid var(--ai-auth-border)', boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
            <Stack align="center" gap="md">
              <Box style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(9,141,147,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCheck size={24} color="#098d93" />
              </Box>
              <Text fw={700} size="lg" c="#e9ecef" ta="center">Cadastro realizado!</Text>
              <Text size="sm" c="#6c757d" ta="center">
                Sua solicitação esta pendente de aprovação pelo gestor responsável.
                Você recebera um email quando sua conta for aprovada.
              </Text>
              <Button
                variant="subtle" color="teal" mt="md"
                onClick={() => navigate('/login')}
              >
                Voltar ao login
              </Button>
            </Stack>
          </Card>
        </Box>
      </Box>
    )
  }

  return (
    <Box style={{ minHeight: '100vh', background: 'var(--ai-auth-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <Box style={{ position: 'absolute', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, background: 'radial-gradient(circle, rgba(9,141,147,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Box style={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}>
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text size="xs" c="rgba(255,255,255,0.35)" mt={8} fw={600} tt="uppercase" style={{ letterSpacing: '0.08em' }}>
            Cadastro de Acesso
          </Text>
        </Stack>

        <Card radius="md" p="xl" style={{ backgroundColor: 'var(--ai-auth-card-bg)', border: '1px solid var(--ai-auth-border)', boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}>
          <Text fw={700} size="lg" c="#e9ecef" mb="xs">Solicitar Acesso</Text>
          <Text size="xs" c="#6c757d" mb="lg">Preencha seus dados para solicitar acesso ao ConsoleAI</Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" radius="sm"
              styles={{ root: { backgroundColor: 'rgba(255,91,87,0.1)', border: '1px solid rgba(255,91,87,0.2)' } }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack gap="sm">
              <Text size="xs" fw={700} c="#098d93" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Dados Pessoais</Text>

              <TextInput
                label="Nome Completo"
                placeholder="Seu nome completo"
                leftSection={<IconUser size={16} />}
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.currentTarget.value })}
                required size="md" styles={inputStyles}
              />
              <PhoneInputIntl
                label="Telefone (WhatsApp)"
                value={form.phone}
                onChange={(e164) => setForm({ ...form, phone: e164 })}
                required
              />
              <TextInput
                label="Endereço (livre)"
                placeholder="Rua, número, bairro, cidade"
                leftSection={<IconMapPin size={16} />}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.currentTarget.value })}
                required size="md" styles={inputStyles}
              />
              {/* Endereço estruturado (D-UX3) — CEP preenche logradouro/bairro/cidade/uf via ViaCEP. */}
              <CepInput
                value={form.cep}
                onChange={(cep) => setForm((f) => ({ ...f, cep }))}
                onResolved={(a) => setForm((f) => ({
                  ...f, logradouro: a.logradouro, bairro: a.bairro, cidade: a.cidade, uf: a.uf,
                }))}
                size="md"
                styles={inputStyles}
              />
              <TextInput
                label="Logradouro" value={form.logradouro}
                onChange={(e) => setForm({ ...form, logradouro: e.currentTarget.value })}
                size="md" styles={inputStyles}
              />
              <Group grow>
                <TextInput
                  label="Número" value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.currentTarget.value })}
                  size="md" styles={inputStyles}
                />
                <TextInput
                  label="Complemento" value={form.complemento}
                  onChange={(e) => setForm({ ...form, complemento: e.currentTarget.value })}
                  size="md" styles={inputStyles}
                />
              </Group>
              <TextInput
                label="Bairro" value={form.bairro}
                onChange={(e) => setForm({ ...form, bairro: e.currentTarget.value })}
                size="md" styles={inputStyles}
              />
              <Group grow>
                <TextInput
                  label="Cidade" value={form.cidade}
                  onChange={(e) => setForm({ ...form, cidade: e.currentTarget.value })}
                  size="md" styles={inputStyles}
                />
                <TextInput
                  label="UF" maxLength={2} value={form.uf}
                  onChange={(e) => setForm({ ...form, uf: e.currentTarget.value.toUpperCase() })}
                  size="md" styles={inputStyles}
                />
              </Group>

              <Text size="xs" fw={700} c="#098d93" tt="uppercase" mt="sm" style={{ letterSpacing: '0.05em' }}>Dados da Empresa</Text>

              <TextInput
                label="Email Institucional"
                placeholder="nome.sobrenome@amazonasinovare.com.br"
                leftSection={<IconMail size={16} />}
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.currentTarget.value })
                  validateEmail(e.currentTarget.value)
                }}
                error={emailError}
                required size="md" styles={inputStyles}
              />
              <Select
                label="Organização"
                placeholder="Selecione a organização"
                leftSection={<IconBuilding size={16} />}
                data={organizations.map(o => ({ value: String(o.id), label: o.name }))}
                value={form.organization_id}
                onChange={(v) => setForm({ ...form, organization_id: v || '' })}
                required size="md"
                styles={{
                  label: inputStyles.label,
                  input: inputStyles.input,
                  dropdown: { backgroundColor: 'var(--ai-auth-card-bg)', borderColor: 'var(--ai-auth-border)' },
                  option: { color: '#e9ecef', '&[data-selected]': { backgroundColor: 'var(--ai-brand-verde)' } },
                }}
              />
              <TextInput
                label="Número da Matrícula"
                placeholder="Sua matrícula"
                leftSection={<IconId size={16} />}
                value={form.employee_number}
                onChange={(e) => setForm({ ...form, employee_number: e.currentTarget.value })}
                required size="md" styles={inputStyles}
              />

              <Button
                type="submit" fullWidth loading={loading} mt={8} size="md" radius="sm"
                style={{ backgroundColor: 'var(--ai-brand-verde)', border: 'none', fontWeight: 700, fontSize: 13, letterSpacing: '0.02em', transition: 'all var(--ai-transition-base)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#077a80' }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)' }}
              >
                Solicitar Acesso
              </Button>
            </Stack>
          </form>

          <Group justify="center" mt="lg">
            <Text size="xs" c="#098d93" fw={600} style={{ cursor: 'pointer' }} onClick={() => navigate('/login')}>
              Já possui conta? Entrar
            </Text>
          </Group>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
